import { useState, useEffect } from 'react';
import { 
 X, Zap
} from 'lucide-react';
import languageData from "../languages.json"
import { ToastContainer, toast } from "react-toastify";
import CreatableSelect from "react-select/creatable";
import configData from "../config.json";
import { getToken } from "../utils/auth";
import { handleUpload } from "../functions/upload_to_MM.jsx";

export function Step1PlanRightColumn ({ 
    audioData,
    setAudio,
    bannerData,
    setBanner,
    CompanyName,
    logoUrl,
    marketingGoals,
    setMarketingGoals,
    voice,
    setVoice,
    targetAudience,
    setTargetAudience,
    vibeAndTone,
    setVibeAndTone,
    marketingGoalsDropdown,
    targetAudienceDropdown,
    vibeAndToneDropdown,
    voicesDropdown,
    brandColors,
    logos,
    companyName,
    language, 
    onGenerate}){

    const toOption = (item) => {
        if (!item) return null;
        if (typeof item === "string") return { value: item, label: item };
        const value = item.value ?? item.label ?? item.name ?? String(item);
        const label = item.label ?? item.value ?? item.name ?? String(item);
        return { value, label };
    };

    const normalizeOptions = (items) => {
        if (!Array.isArray(items)) return [];
        const seen = new Set();
        const out = [];
        for (const raw of items) {
            const opt = toOption(raw);
            if (!opt?.value) continue;
            const key = String(opt.value);
            if (seen.has(key)) continue;
            seen.add(key);
            out.push(opt);
        }
        return out;
    };

    const toValue = (arr) =>
        (Array.isArray(arr) ? arr : []).map((v) => ({ value: v, label: v }));

    const updateFromSelect = (newValue, setList) => {
        const vals = (newValue || [])
            .map((o) => (typeof o === "string" ? o : o?.value))
            .filter(Boolean);
        // de-dupe, preserve order
        setList(Array.from(new Set(vals)));
    };

    const selectStyles = {
        control: (base, state) => ({
            ...base,
            borderColor: state.isFocused ? "#3B82F6" : "#93C5FD", // blue-500 / blue-300
            boxShadow: state.isFocused ? "0 0 0 1px #3B82F6" : "none",
            minHeight: 42,
        }),
        multiValue: (base) => ({
            ...base,
            backgroundColor: "#DBEAFE", // blue-100
            border: "1px solid #BFDBFE", // blue-200
        }),
        multiValueLabel: (base) => ({ ...base, color: "#1E40AF", fontWeight: 600 }), // blue-800
        multiValueRemove: (base) => ({ ...base, color: "#1E40AF" }),
        menu: (base) => ({ ...base, zIndex: 50 }),
    };

    const buildInstructionString = () => {
        return `
        Text Goals: ${marketingGoals.join(", ")}. Target Audience: ${targetAudience.join(", ")}. Vibe & Tone: ${vibeAndTone.join(", ")}.`.trim();
    };
    

    return(

        <div className="flex-1 flex flex-col gap-6 p-6 bg-blue-50 rounded-xl border border-blue-100 custom-scrollbar">
            <h4 className="font-bold text-blue-900 border-b border-blue-200 pb-2">2. Campaign Details</h4>

            {/* Goals */}
            <div>
                <label className="block text-xs font-bold text-blue-700 uppercase mb-1">Marketing Goals</label>
                <CreatableSelect
                    isMulti
                    isClearable
                    placeholder="Select or type to add..."
                    options={normalizeOptions(marketingGoalsDropdown)}
                    value={toValue(marketingGoals)}
                    onChange={(newValue) => updateFromSelect(newValue, setMarketingGoals)}
                    onCreateOption={(input) => {
                        const val = String(input || "").trim();
                        if (!val) return;
                        setMarketingGoals(Array.from(new Set([...(marketingGoals || []), val])));
                    }}
                    styles={selectStyles}
                />
            </div>

            {/* vibeAndTone */}
            <div>
                <label className="block text-xs font-bold text-blue-700 uppercase mb-1">Vibe & Tone</label>
                <CreatableSelect
                    isMulti
                    isClearable
                    placeholder="Select or type to add..."
                    options={normalizeOptions(vibeAndToneDropdown)}
                    value={toValue(vibeAndTone)}
                    onChange={(newValue) => updateFromSelect(newValue, setVibeAndTone)}
                    onCreateOption={(input) => {
                        const val = String(input || "").trim();
                        if (!val) return;
                        setVibeAndTone(Array.from(new Set([...(vibeAndTone || []), val])));
                    }}
                    styles={selectStyles}
                />
            </div>

            {/* Voice */}
            <div>
                <label className="block text-xs font-bold text-blue-700 uppercase mb-1">Voice</label>
                <select
                value={voice}
                onChange={(e) => setVoice(e.target.value)}
                disabled={!voicesDropdown?.length}
                className="w-full text-sm border border-blue-300 rounded p-2 text-blue-900 bg-white hover:border-blue-500 cursor-pointer focus:outline-none focus:border-blue-500 shadow-sm disabled:opacity-60 disabled:cursor-not-allowed"
                >
                {!voicesDropdown?.length ? (
                    <option value="">No voices configured</option>
                ) : (
                    voicesDropdown.map((v) => (
                        <option key={v.value} value={v.value}>
                            {v.label}
                        </option>
                    ))
                )}
                </select>
            </div>

            {/* Target Audience */}
            <div>
                <label className="block text-xs font-bold text-blue-700 uppercase mb-1">Target Audience</label>
                <CreatableSelect
                    isMulti
                    isClearable
                    placeholder="Select or type to add..."
                    options={normalizeOptions(targetAudienceDropdown)}
                    value={toValue(targetAudience)}
                    onChange={(newValue) => updateFromSelect(newValue, setTargetAudience)}
                    onCreateOption={(input) => {
                        const val = String(input || "").trim();
                        if (!val) return;
                        setTargetAudience(Array.from(new Set([...(targetAudience || []), val])));
                    }}
                    styles={selectStyles}
                />
            </div>

            {/* Generate Button */}
            <div className="pt-2 mt-auto">
                <button
                onClick={() => {
                    void handleUpload(
                        {
                            marketingGoals,
                            vibeAndTone,
                            targetAudience,
                            logos: logos || [],
                            company_name: companyName || "",
                            voice,
                            language,
                        },
                        { configData, getToken }
                    );

                    setAudio({
                        ...audioData,
                        chain_name: CompanyName ?? "",
                        text_instructions: buildInstructionString(), // update text_instructions
                        voice_instructions: `${languageData.VOICE_DICT[language]} ${vibeAndTone.join(", ")}:`,
                        voice_name: voice
                    });

                    setBanner({
                        ...bannerData,
                        chain_name: CompanyName ?? "",
                        logo_url: logoUrl,
                        banner_director_instructions: `The banner's marketing goals are ${marketingGoals.join(", ")}, the vibe and tone of the banner are ${vibeAndTone.join(", ")}, and the target audience of the banner are ${targetAudience.join(", ")}, please use the language: ${language}.`,
                        video_director_instructions: `The video's marketing goals are ${marketingGoals.join(", ")}, the vibe and tone of the video are ${vibeAndTone.join(", ")}, and the target audience of the video are ${targetAudience.join(", ")}, please use the language: ${language}.`
                    })
                    onGenerate();
                }}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-4 rounded-lg shadow-md transition-all transform active:scale-95 flex items-center justify-center gap-2"
                >
                <span>Generate Assets</span>
                <Zap size={20} />
                </button>
            </div>
        </div>
    )
}
//  And use the following colors with the Hex code: ${brandColors}, do not include the hex code text only use the colors